import { classToPlain, Exclude, Expose, Type } from "class-transformer";
import md5 from "crypto-js/md5";
import { Expenditure, Unimi } from "./Decoding";
import { FileModel } from "./FileModel";
import { INPROGRESS } from "./Contract";

export interface ChangeRequestPaths {
    "form-a": number;
    "form-b": number;
    "form-b-line-items": number;
    "form-further-info": number;
}

export class ChangeRequest extends FileModel {
    app_status!: string;
    subject_id!: number;
    has_further_info!: number;
    can_change!: number;
    contract_code!: string;
    cr_date!: number;
    contract_id!: number;
    description?: string;
    cr_reference!: string;
    application_id!: number;
    status!: string;
    //status_code!: string;
    application_status_code!: string;

    identification_code!: string;
    project_app_id!: number;
    project_title!: string;
    cod_modu!: string;
    type_modu!: string | null;
    measure!: string;
    proj_dt_start!: string;
    proj_dt_end!: string;
    total_pub_elig_amount!: number;
    pub_elig!: number;
    priv_share!: number;
    total_proj_cost!: number;
    remarks: string | null = null;
    farmer_informed: boolean | null = null;
    paths!: ChangeRequestPaths;
    can_submit = false;
    can_pending = false;

    toString(): string {
        return `${this.cod_modu} | ${this.identification_code} | ${Math.abs(this.application_id)} | ${
            this.project_title
        }`;
    }

    @Exclude() private _can_edit!: number;
    @Expose()
    get can_edit(): number {
        return this._can_edit;
    }
    set can_edit(can_edit: number) {
        this._can_edit = can_edit;
    }

    get canEdit(): boolean {
        return !!this._can_edit;
    }

    @Exclude() private _can_skip_files!: number;
    @Expose()
    get can_skip_files(): number {
        return this._can_skip_files;
    }
    set can_skip_files(can_skip_files: number) {
        this._can_skip_files = can_skip_files;
    }

    get canSkipFiles(): boolean {
        return !!this._can_skip_files;
    }

    @Exclude()
    get isInProgress(): boolean {
        return this.application_status_code === INPROGRESS;
    }

    @Exclude()
    get appStatus(): string {
        return this.isInProgress ? "DRAFT" : this.app_status;
    }
}

export class FormA extends FileModel {
    application_id!: number;
    subject_id!: number;
    justification_notes: string | null = null;
    approved!: boolean;

    @Exclude()
    get isValid(): string[] {
        const ret: string[] = [];
        if (!(this._extend_date instanceof Date)) {
            ret.push("extend_date");
        }

        if (
            typeof this.justification_notes !== "string" ||
            (typeof this.justification_notes === "string" && this.justification_notes.trim().length === 0)
        ) {
            ret.push("justification_notes");
        }

        return ret;
    }

    @Exclude() private _extend_date: Date | null = null;

    set extend_date(date: string | null) {
        this._extend_date = date ? new Date(Number(date) * 1000) : null;
    }

    @Expose()
    get extend_date(): string | null {
        return this._extend_date ? this._extend_date.toISOString() : null;
    }

    @Exclude() private _end_date: Date | null = null;

    set end_date(date: string | null) {
        this._end_date = date ? new Date(Number(date) * 1000) : null;
    }

    @Expose()
    get end_date(): string | null {
        return this._end_date ? this._end_date.toLocaleDateString("en-GB") : null;
    }

    @Exclude()
    get extend_date_as_date(): Date | null {
        return this._extend_date;
    }

    set extend_date_as_date(date: Date | null) {
        this._extend_date = date;
    }

    @Exclude()
    get end_date_as_date(): Date | null {
        return this._end_date;
    }

    set end_date_as_date(date: Date | null) {
        this._end_date = date;
    }

    toObject(): Record<string, string | number> {
        return classToPlain(this);
    }
}

export class FormB /* extends FileModel */ {
    application_id!: number;
    subject_id!: number;

    brief_desc: string | null = null;
    justif_desc: string | null = null;
    private has_risks: boolean | null = null;
    risks_desc: string | null = null;
    outcome_desc: string | null = null;
    private has_permit: boolean | null = null;
    permit_desc: string | null = null; //! XXX not used
    permit_steps: string | null = null;
    private has_components: boolean | null = null;

    @Exclude()
    get isValid(): string[] {
        const ret: string[] = [];
        if (
            typeof this.brief_desc !== "string" ||
            (typeof this.brief_desc === "string" && this.brief_desc.trim().length === 0)
        ) {
            ret.push("brief_desc");
        }

        if (
            typeof this.justif_desc !== "string" ||
            (typeof this.justif_desc === "string" && this.justif_desc.trim().length === 0)
        ) {
            ret.push("justif_desc");
        }

        if (typeof this.has_risks !== "boolean") {
            ret.push("has_risks");
        }

        if (
            (this.has_risks && typeof this.risks_desc !== "string") ||
            (this.has_risks && typeof this.risks_desc === "string" && this.risks_desc.trim().length === 0)
        ) {
            ret.push("risks_desc");
        }

        if (
            typeof this.outcome_desc !== "string" ||
            (typeof this.outcome_desc === "string" && this.outcome_desc.trim().length === 0)
        ) {
            ret.push("outcome_desc");
        }

        if (typeof this.has_permit !== "boolean") {
            ret.push("has_permit");
        }

        if (
            (this.has_permit && typeof this.permit_steps !== "string") ||
            (this.has_permit && typeof this.permit_steps === "string" && this.permit_steps.trim().length === 0)
        ) {
            ret.push("permit_steps");
        }

        if (typeof this.has_components !== "boolean") {
            ret.push("has_components");
        }

        return ret;
    }

    set hasRisks(value: string | null) {
        try {
            this.has_risks = value ? JSON.parse(value) ?? null : null;
        } catch (e) {
            this.has_risks = null;
        }
    }

    @Exclude()
    get hasRisks(): string | null {
        return typeof this.has_risks === "boolean" ? this.has_risks.toString() : null;
    }

    set hasPermit(value: string | null) {
        try {
            this.has_permit = value ? JSON.parse(value) ?? null : null;
        } catch (e) {
            this.has_permit = null;
        }
    }

    @Exclude()
    get hasPermit(): string | null {
        return typeof this.has_permit === "boolean" ? this.has_permit.toString() : null;
    }

    set hasComponents(value: string | null) {
        try {
            this.has_components = value ? JSON.parse(value) ?? null : null;
        } catch (e) {
            this.has_components = null;
        }
    }

    @Exclude()
    get hasComponents(): string | null {
        return typeof this.has_components === "boolean" ? this.has_components.toString() : null;
    }
}

export class Quotation {
    item_id!: number;
    application_id!: number;
    subject_id!: number;
    component_id!: number;
    quotation_id!: number;
    quotation: string | null = null;
    net_amount: number | null = null;
    vat_amount: number | null = null;
    eligible_net_amount: number | null = null;
    eligible_vat_amount: number | null = null;
    // notes: string | null = null;
    cr_notes: string | null = null;
    um_code: string | null = null;
    um_sub_code: string | null = null;
    um_description: string | null = null;
    quantity: number | null = null;

    @Exclude()
    private _touched = false;

    @Exclude()
    set touched(value: boolean) {
        this._touched = value;
    }

    @Exclude()
    set umimis(umimis: Unimi | null) {
        if (umimis) {
            this.um_code = umimis.unimis_code;
            this.um_sub_code = umimis.unimis_sub_code;
            this.um_description = umimis.unimis_description;
        } else {
            this.um_code = null;
            this.um_sub_code = null;
            this.um_description = null;
        }
    }

    @Exclude()
    get isValid(): string[] {
        const ret: string[] = [];
        if (
            typeof this.quotation !== "string" ||
            (typeof this.quotation === "string" && this.quotation.trim().length === 0)
        ) {
            ret.push("quotation");
        }

        // if (typeof this.vat_amount !== "number" || typeof this.vat_amount === "number" && this.vat_amount < 0) {
        //     ret.push("vat_amount");
        // }

        // if (typeof this.net_amount !== "number" || typeof this.net_amount === "number" && this.net_amount < 0) {
        //     ret.push("net_amount");
        // }

        if (
            typeof this.eligible_vat_amount !== "number" ||
            (typeof this.eligible_vat_amount === "number" && this.eligible_vat_amount < 0)
        ) {
            ret.push("eligible_vat_amount");
        }

        if (
            typeof this.eligible_net_amount !== "number" ||
            (typeof this.eligible_net_amount === "number" && this.eligible_net_amount < 0)
        ) {
            ret.push("eligible_net_amount");
        }

        // if (typeof this.notes !== "string" || (typeof this.notes === "string" && this.notes.trim().length === 0)) {
        //     ret.push("notes");
        // }

        // if (typeof this.cr_notes !== "string" || typeof this.cr_notes === "string" && this.cr_notes.trim().length === 0) {
        //     ret.push("cr_notes");
        // }

        return this._touched ? ret : [];
    }
}

export class FormBLineItem extends FileModel {
    application_id!: number;
    subject_id!: number;
    item_id!: number;
    action_id!: number;
    net_amount: number | null = null;
    vat_amount: number | null = null;
    eligible_net_amount: number | null = null;
    eligible_vat_amount: number | null = null;
    saved_amount: number | null = null;
    trasf_saved_amount: number | null = null;
    payed_amount: number | null = null;
    notes: string | null = null;
    fg_approved: string | null = null;
    fkid_folder: number | null = null;

    action_description: string | null = null;

    // activity refers to "Type to expenditure"
    activity_code: string | null = null;
    activity_sub_code: string | null = null;
    activity_description: string | null = null;

    proposal_code: string | null = null;
    proposal_sub_code: string | null = null;
    proposal_description: string | null = null;

    component: string | null = null;
    @Type(() => Quotation)
    quotations: Quotation[] = [];

    @Exclude({ toPlainOnly: true })
    private _edited = false;

    @Exclude()
    private _touched = false;

    @Exclude()
    set touched(value: boolean) {
        this._touched = value;
    }

    set edited(edited: boolean) {
        this._edited = edited;
    }

    @Expose()
    get edited(): boolean {
        if (this._edited) {
            return true;
        }
        const hash = FormBLineItem.toHash(this);
        return this.hash !== hash;
    }

    @Exclude({ toPlainOnly: true })
    hash!: string;

    @Exclude()
    set activity(activity: Expenditure | null) {
        if (activity) {
            this.activity_code = activity.expenditure_code;
            this.activity_sub_code = activity.expenditure_sub_code;
            this.activity_description = activity.expenditure_description;
        } else {
            this.activity_code = null;
            this.activity_sub_code = null;
            this.activity_description = null;
        }
    }

    @Exclude()
    static toHash({
        file_ids,
        upload_urls,
        notes,
        eligible_vat_amount,
        eligible_net_amount,
        component,
        activity_description,
        action_description,
        activity_code,
        quotations: quotationsItems
    }: FormBLineItem): string {
        const quotations = quotationsItems.map(
            ({
                cr_notes,
                // notes,
                eligible_vat_amount,
                eligible_net_amount,
                vat_amount,
                net_amount,
                quantity,
                um_code,
                um_sub_code,
                um_description,
                quotation
            }) => {
                return {
                    cr_notes,
                    notes,
                    eligible_vat_amount,
                    eligible_net_amount,
                    vat_amount,
                    net_amount,
                    quantity,
                    um_code,
                    um_sub_code,
                    um_description,
                    quotation
                };
            }
        );

        const asString = JSON.stringify({
            file_ids,
            upload_urls,
            notes,
            eligible_vat_amount,
            eligible_net_amount,
            component,
            activity_description,
            action_description,
            activity_code,
            quotations
        });
        return md5(asString).toString();
    }

    @Exclude()
    get isValid(): string[] {
        const isValid = this._touched
            ? this.quotations.map((q, i) => q.isValid.map(e => `quotations.${i}.${e}`)).flat()
            : [];
        const ret = [...isValid];

        // if (typeof this.net_amount !== "number") {
        //     ret.push("net_amount")
        // }
        // if (typeof this.vat_amount !== "number") {
        //     ret.push("vat_amount")
        // }

        // if (typeof this.component !== "string" || typeof this.component === "string" && this.component.trim().length === 0) {
        //     ret.push("component")
        // }
        // if (typeof this.eligible_net_amount !== "number" || typeof this.eligible_net_amount === "number" && this.eligible_net_amount < 0) {
        //     ret.push("eligible_net_amount")
        // }

        // if (typeof this.eligible_vat_amount !== "number" || typeof this.eligible_vat_amount === "number" && this.eligible_vat_amount < 0) {
        //     ret.push("eligible_vat_amount")
        // }
        return ret;
    }
}

export class FormBLineItems {
    @Type(() => FormBLineItem)
    lineItems: FormBLineItem[] = [];

    @Exclude()
    get isValid(): string[] {
        const isValid = this.lineItems.map((q, i) => q.isValid.map(e => `lineItems.${i}.${e}`)).flat();
        return [...isValid];
    }
}

export class FurtherInfo {
    application_id!: number;
    subject_id!: number;
    remarks!: string;
    answer!: string;
    question!: string;
    can_answer!: 0 | 1;

    @Exclude()
    get isValid(): boolean {
        return true;
    }
}

export class FurtherInfos extends FileModel {
    @Type(() => FurtherInfo)
    furtherInfos: FurtherInfo[] = [];

    @Exclude()
    get isValid(): boolean {
        return this.furtherInfos.every(info => info.isValid);
    }
}
